import type { QuestionGroup, Summary } from './types';
import { CATEGORY_COLORS } from './sankey';
import { getChartRuntime } from './chartRuntime';
import { formatPercentValue } from './utils';
import { BAR_PALETTE } from './palettes';

export function renderStackedSummary(
  group: QuestionGroup,
  questionLookup: Map<string, { id: string; question: string }>
): HTMLElement | null {
  const summaries: Array<{ key: string; summary: Summary; question?: { id: string; question: string } }> = group.keys
    .map((key) => ({ key, summary: (window as any).__ECE25Summaries?.[key] as Summary, question: questionLookup.get(key) as any }))
    .filter((entry) => entry.summary) as any;

  if (summaries.length < 1) return null;

  const termLabels = group.labels !== undefined ? group.labels : summaries.map((entry, index) => {
    const match = entry.question?.question.match(/Coop\s*(\d+)/i);
    return match ? `Co-op ${match[1]}` : `Term ${index + 1}`;
  });

  const locations: string[] = [];
  const seen = new Set<string>();
  summaries.forEach(({ summary }) => {
    const preferred = summary.order ?? summary.labels ?? [];
    preferred.forEach((label) => { if (!seen.has(label)) { seen.add(label); locations.push(label); } });
    (summary.labels ?? []).forEach((label) => { if (!seen.has(label)) { seen.add(label); locations.push(label); } });
  });
  if (!locations.length) return null;

  const termTotals = summaries.map(({ summary }) => {
    if (summary.n && summary.n > 0) return summary.n;
    const countsSum = summary.counts?.reduce((sum, value) => sum + value, 0) ?? 0;
    return countsSum;
  });

  const datasets = locations.map((location, index) => {
    const data = summaries.map(({ summary }) => {
      const labelIndex = summary.labels?.indexOf(location) ?? -1;
      return labelIndex >= 0 ? summary.counts?.[labelIndex] ?? 0 : 0;
    });
    const normalized = data.map((value, termIndex) => {
      const total = termTotals[termIndex] ?? 0;
      if (!total) return 0;
      let pct = (value / total) * 100;
      if (!Number.isFinite(pct)) pct = 0;
      if (pct < 0) pct = 0;
      if (pct > 100) pct = 100;
      return pct;
    });
    return {
      label: location,
      data: normalized,
      rawCounts: data,
      termTotals,
      backgroundColor: (CATEGORY_COLORS as any)[location] ?? BAR_PALETTE[index % BAR_PALETTE.length],
      stack: 'locations',
    } as any;
  });

  const container = document.createElement('div');
  container.className = 'chart chart--bar';
  const canvas = document.createElement('canvas');
  container.appendChild(canvas);

  const ChartRuntime = getChartRuntime();
  const chart = new ChartRuntime(canvas, {
    type: 'bar',
    data: { labels: termLabels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 600 },
      indexAxis: 'y',
      scales: {
        // Value axis (percent)
        x: {
          stacked: true,
          ticks: {
            color: '#475569',
            font: { size: 12 },
            callback(value: string | number) {
              const numeric = typeof value === 'string' ? Number(value) : value;
              if (!Number.isFinite(numeric)) return value as any;
              return `${numeric}%`;
            },
          },
          min: 0,
          max: 100,
          beginAtZero: true,
          grid: { color: 'rgba(148, 163, 184, 0.18)', drawBorder: false },
        },
        // Category axis (terms)
        y: {
          stacked: true,
          ticks: { color: '#475569', font: { size: 12 } },
          grid: { color: 'rgba(148, 163, 184, 0.18)', drawBorder: false },
        },
      },
      plugins: {
        legend: { position: 'top', labels: { color: '#475569' } },
        tooltip: {
          callbacks: {
            label(context: any) {
              const dataset = context.dataset as any;
              const percent = context.raw as number;
              const rawCount = dataset?.rawCounts?.[context.dataIndex] ?? 0;
              const total = dataset?.termTotals?.[context.dataIndex] ?? 0;
              const pctLabel = formatPercentValue(percent);
              return `${dataset.label}: ${rawCount} of ${total} respondents (${pctLabel})`;
            },
          },
        },
      },
    },
  });

  (globalThis as any).__activeCharts = (globalThis as any).__activeCharts || [];
  (globalThis as any).__activeCharts.push(chart);
  return container;
}
